import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Popconfirm, Select, Space, Table, message } from "antd";
import {
  createNutritionTemplate,
  deleteNutritionTemplate,
  fetchNutritionLookups,
  fetchNutritionTemplate,
  fetchNutritionTemplates,
  searchNutritionProducts,
  updateNutritionTemplate
} from "./nutrition-api";
import {
  emptyLookups,
  field,
  lookupOptions,
  normalizeLookups,
  normalizeProduct,
  numberField,
  showError
} from "./nutrition-utils";
import NutritionDrawer, { DrawerFooter } from "./nutrition-drawer";
import "./nutrition.css";

const normalizeLine = (row) => ({
  idStr: numberField(row, "idStr") || undefined,
  idTov: numberField(row, "idTov") || undefined,
  tovName: field(row, "tovName", ""),
  qty: numberField(row, "qty", 1),
  part: numberField(row, "part", 1)
});

const NutritionTemplates = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [lookups, setLookups] = useState(emptyLookups);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const searchSeq = useRef(0);
  const searchTimer = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [templates, lookupData] = await Promise.all([
        fetchNutritionTemplates(),
        fetchNutritionLookups()
      ]);
      setRows(Array.isArray(templates) ? templates : []);
      setLookups(normalizeLookups(lookupData));
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить шаблоны");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => void load(), [load]);

  const searchProducts = useCallback((query = "") => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      setProductLoading(true);
      try {
        const data = await searchNutritionProducts(query);
        if (seq === searchSeq.current) {
          setProducts((Array.isArray(data) ? data : []).map(normalizeProduct).filter((x) => x.idTov));
        }
      } catch {
        if (seq === searchSeq.current) setProducts([]);
      } finally {
        if (seq === searchSeq.current) setProductLoading(false);
      }
    }, 300);
  }, []);

  const showEditor = async (id) => {
    setEditingId(id || null);
    setOpen(true);
    setProducts([]);
    if (!id) {
      form.resetFields();
      form.setFieldsValue({ mealTypeId: lookups.mealTypes[0]?.id, lines: [] });
      return;
    }
    setSaving(true);
    try {
      const row = await fetchNutritionTemplate(id);
      const lines = (field(row, "lines", []) || []).map(normalizeLine);
      setProducts(lines.map((x) => ({ idTov: x.idTov, name: x.tovName })));
      form.setFieldsValue({
        name: field(row, "name", ""),
        mealTypeId: numberField(row, "mealTypeId"),
        lines
      });
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить шаблон");
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const productOptions = useMemo(
    () => products.map((x) => ({ value: x.idTov, label: x.name })),
    [products]
  );

  const save = async () => {
    const values = await form.validateFields();
    const body = {
      name: values.name.trim(),
      mealTypeId: values.mealTypeId,
      lines: (values.lines || []).map((line) => ({
        idStr: line.idStr || null,
        idTov: line.idTov,
        qty: line.qty,
        part: line.part
      }))
    };
    setSaving(true);
    try {
      if (editingId) await updateNutritionTemplate(editingId, body);
      else await createNutritionTemplate(body);
      messageApi.success("Шаблон сохранён");
      setOpen(false);
      await load();
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить шаблон");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "Название", dataIndex: "name", render: (_, r) => field(r, "name", "") },
    {
      title: "Вид питания",
      dataIndex: "mealTypeName",
      width: 220,
      render: (_, r) => field(r, "mealTypeName", "")
    },
    {
      title: "Позиций",
      width: 100,
      render: (_, r) => (field(r, "lines", []) || []).length
    },
    {
      title: "",
      width: 60,
      render: (_, r) => (
        <Popconfirm
          title="Удалить шаблон?"
          onConfirm={async () => {
            try {
              await deleteNutritionTemplate(numberField(r, "id"));
              messageApi.success("Шаблон удалён");
              await load();
            } catch (error) {
              showError(messageApi, error, "Не удалось удалить шаблон");
            }
          }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      )
    }
  ];

  return (
    <div className="nutrition-page">
      {contextHolder}
      <div className="nutrition-toolbar">
        <h1 className="nutrition-title">Шаблоны меню</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showEditor(null)}>
          Создать
        </Button>
      </div>
      <div className="nutrition-table-wrap">
        <Table
          rowKey={(r) => numberField(r, "id")}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          sticky
          onRow={(r) => ({ className: "nutrition-cost-day", onClick: () => showEditor(numberField(r, "id")) })}
        />
      </div>
      <NutritionDrawer
        open={open}
        title={editingId ? "Шаблон меню" : "Новый шаблон"}
        width="min(900px, calc(100vw - 80px))"
        onClose={() => setOpen(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, whitespace: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="mealTypeId" label="Вид питания" rules={[{ required: true }]}>
            <Select options={lookupOptions(lookups.mealTypes)} />
          </Form.Item>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map((item) => (
                  <Space key={item.key} align="start" style={{ display: "flex" }}>
                    <Form.Item name={[item.name, "idStr"]} hidden><InputNumber /></Form.Item>
                    <Form.Item name={[item.name, "tovName"]} hidden><Input /></Form.Item>
                    <Form.Item name={[item.name, "idTov"]} rules={[{ required: true, message: "Выберите продукт" }]}>
                      <Select
                        showSearch
                        filterOption={false}
                        loading={productLoading}
                        options={productOptions}
                        placeholder="Поиск продукта"
                        style={{ width: 330 }}
                        onFocus={() => searchProducts("")}
                        onSearch={searchProducts}
                        onChange={(value, option) => {
                          form.setFieldValue(["lines", item.name, "tovName"], option?.label || "");
                        }}
                      />
                    </Form.Item>
                    <Form.Item name={[item.name, "qty"]} rules={[{ required: true }]}>
                      <InputNumber min={0.001} precision={3} placeholder="Кол-во" />
                    </Form.Item>
                    <Form.Item name={[item.name, "part"]} rules={[{ required: true }]}>
                      <Select options={lookupOptions(lookups.parts)} placeholder="Приём" style={{ width: 150 }} />
                    </Form.Item>
                    <Button danger icon={<DeleteOutlined />} onClick={() => remove(item.name)} />
                  </Space>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ qty: 1, part: lookups.parts[0]?.id || 1 })}>
                  Добавить продукт
                </Button>
              </>
            )}
          </Form.List>
          <DrawerFooter onSave={save} onClose={() => setOpen(false)} saving={saving} />
        </Form>
      </NutritionDrawer>
    </div>
  );
};

export default NutritionTemplates;
